import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactComponent as Logo } from '../assets/images/logo.svg';
import { 
  FaBox, 
  FaHome, 
  FaUtensils, 
  FaComment, 
  FaChartLine, 
  FaListAlt,
  FaFish
} from 'react-icons/fa';
import { GiHerbsBundle } from "react-icons/gi";

const AdminAppBar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const getButtonColor = (path) => {
    const colorMap = {
      '/stats': '#8b5cf6',
      '/admin/page': '#06b6d4',
      '/ViewOrdersScreen': '#10b981',
      '/AddGuppyScreen': '#06b6d4',
      '/AddAquariumScreen': '#3b82f6',
      '/AddPlantScreen': '#22c55e',
      '/AddFoodScreen': '#f97316',
      '/AdminHomeScreen': '#10b981'
    };
    return colorMap[path] || '#06b6d4';
  };

  const getRgbFromHex = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 6, g: 182, b: 212 };
  };

  const buttonStyle = (active, color) => {
    const rgb = getRgbFromHex(color);
    return {
      backgroundColor: active ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2)` : 'rgba(51, 65, 85, 0.3)',
      border: `1.5px solid ${active ? color : 'rgba(71, 85, 105, 0.4)'}`,
      borderRadius: '14px',
      padding: '12px',
      cursor: 'pointer',
      transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: active ? color : '#94a3b8',
      boxShadow: active 
        ? `0 4px 16px ${color}40, inset 0 1px 0 rgba(255, 255, 255, 0.1)` 
        : '0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
      position: 'relative',
      overflow: 'hidden',
      minWidth: '44px',
      height: '44px',
      backdropFilter: 'blur(10px)'
    };
  };

  const handleButtonHover = (e, color, path) => {
    if (!isActive(path)) {
      const rgb = getRgbFromHex(color);
      e.currentTarget.style.backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2)`;
      e.currentTarget.style.borderColor = color;
      e.currentTarget.style.transform = 'translateY(-3px) scale(1.05)';
      e.currentTarget.style.boxShadow = `0 8px 24px ${color}50, inset 0 1px 0 rgba(255, 255, 255, 0.15)`;
      e.currentTarget.style.color = color;
    }
  };

  const handleButtonLeave = (e, path) => {
    if (!isActive(path)) {
      e.currentTarget.style.backgroundColor = 'rgba(51, 65, 85, 0.3)';
      e.currentTarget.style.borderColor = 'rgba(71, 85, 105, 0.4)';
      e.currentTarget.style.transform = 'translateY(0) scale(1)';
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)';
      e.currentTarget.style.color = '#94a3b8';
    }
  };

  const buttons = [
    { path: '/stats', icon: FaChartLine, title: 'Statistics & Analytics', color: '#8b5cf6' },
    { path: '/admin/page', icon: FaComment, title: 'Chat', color: '#06b6d4' },
    { path: '/ViewOrdersScreen', icon: FaListAlt, title: 'View Orders', color: '#10b981' },
    { path: '/AddGuppyScreen', icon: FaFish, title: 'Add Guppy', color: '#06b6d4' },
    { path: '/AddAquariumScreen', icon: FaBox, title: 'Add Aquarium', color: '#3b82f6' },
    { path: '/AddPlantScreen', icon: GiHerbsBundle, title: 'Add Plant', color: '#22c55e' },
    { path: '/AddFoodScreen', icon: FaUtensils, title: 'Add Food', color: '#f97316' },
    { path: '/AdminHomeScreen', icon: FaHome, title: 'Admin Dashboard', color: '#10b981' }
  ];

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.99), rgba(30, 41, 59, 0.97))',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(51, 65, 85, 0.5)',
      padding: '14px 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
      width: '100%',
      boxSizing: 'border-box',
      minHeight: '72px'
    }}>
      {/* Gradient Overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.05) 0%, rgba(16, 185, 129, 0.05) 50%, rgba(139, 92, 246, 0.05) 100%)',
        pointerEvents: 'none'
      }}></div>

      {/* Left Side: Home Button */}
      <button
        onClick={() => navigate('/')}
        title="Go to Home"
        style={{
          backgroundColor: 'rgba(51, 65, 85, 0.4)',
          border: '1.5px solid rgba(71, 85, 105, 0.5)',
          borderRadius: '14px',
          padding: '12px',
          cursor: 'pointer',
          transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          color: '#cbd5e1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '44px',
          height: '44px',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
          position: 'relative',
          zIndex: 2
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(6, 182, 212, 0.25)';
          e.currentTarget.style.borderColor = '#06b6d4';
          e.currentTarget.style.transform = 'translateY(-3px) scale(1.05)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(6, 182, 212, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
          e.currentTarget.style.color = '#06b6d4';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(51, 65, 85, 0.4)';
          e.currentTarget.style.borderColor = 'rgba(71, 85, 105, 0.5)';
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)';
          e.currentTarget.style.color = '#cbd5e1';
        }}
      >
        <FaHome size={20} />
      </button>

      {/* Center: Enhanced Logo and Title */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '14px',
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2
      }}>
        {/* Logo Container with Glow */}
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '52px',
          height: '52px',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(51, 65, 85, 0.8))',
          backdropFilter: 'blur(15px)',
          border: '1.5px solid rgba(6, 182, 212, 0.4)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(6, 182, 212, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
          e.currentTarget.style.boxShadow = '0 12px 40px rgba(6, 182, 212, 0.5), 0 0 0 1px rgba(6, 182, 212, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
          e.currentTarget.style.borderColor = '#06b6d4';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(6, 182, 212, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
          e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.4)';
        }}
        >
          {/* Glow Effect */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '60px',
            height: '60px',
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.3) 0%, rgba(16, 185, 129, 0.2) 40%, transparent 70%)',
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            animation: 'logoGlow 3s ease-in-out infinite',
            pointerEvents: 'none'
          }}></div>
          <Logo style={{ 
            width: '38px', 
            height: '38px',
            filter: 'drop-shadow(0 4px 12px rgba(6, 182, 212, 0.5))',
            position: 'relative',
            zIndex: 1,
            transition: 'all 0.3s ease'
          }} />
        </div>
        
        {/* Title */}
        <div>
          <h1 style={{ 
            color: '#fff', 
            fontSize: '20px', 
            fontWeight: '900', 
            margin: 0,
            background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 50%, #8b5cf6 100%)',
            backgroundSize: '200% 200%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            letterSpacing: '-0.03em',
            lineHeight: '1.2',
            animation: 'gradientShift 5s ease infinite',
            textShadow: '0 0 40px rgba(16, 185, 129, 0.3)',
            position: 'relative'
          }}>
            Admin Panel
          </h1>
          <div style={{
            width: '60px',
            height: '2px',
            background: 'linear-gradient(90deg, transparent, #10b981, #06b6d4, transparent)',
            marginTop: '4px',
            borderRadius: '1px',
            boxShadow: '0 0 8px rgba(16, 185, 129, 0.4)'
          }}></div>
        </div>
      </div>

      {/* Right Side: Action Buttons Container */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: '8px',
        position: 'relative',
        zIndex: 2,
        flexWrap: 'nowrap',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        maxWidth: 'calc(100% - 200px)',
        paddingRight: '4px'
      }}
      className="admin-appbar-buttons"
      >
        {buttons.map(({ path, icon: Icon, title, color }) => {
          const active = isActive(path);
          return (
            <button
              key={path}
              data-path={path}
              onClick={() => navigate(path)}
              title={title}
              style={buttonStyle(active, color)}
              onMouseEnter={(e) => handleButtonHover(e, color, path)}
              onMouseLeave={(e) => handleButtonLeave(e, path)}
            >
              {/* Active Indicator */}
              {active && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
                  borderRadius: '14px 14px 0 0'
                }}></div>
              )}
              {/* Icon */}
              <Icon 
                size={20} 
                style={{
                  position: 'relative',
                  zIndex: 1,
                  filter: active ? `drop-shadow(0 2px 8px ${color}60)` : 'none',
                  transition: 'all 0.3s ease'
                }}
              />
              {/* Hover Glow Effect */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(circle at center, ${color}20, transparent 70%)`,
                opacity: 0,
                transition: 'opacity 0.3s ease',
                pointerEvents: 'none'
              }}></div>
            </button>
          );
        })}
      </div>

      <style>{`
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes logoGlow {
          0%, 100% { 
            transform: translate(-50%, -50%) scale(1); 
            opacity: 0.4; 
          }
          50% { 
            transform: translate(-50%, -50%) scale(1.15); 
            opacity: 0.7; 
          }
        }

        .admin-appbar-buttons::-webkit-scrollbar {
          display: none;
        }

        .admin-appbar-buttons button:hover > div:last-child {
          opacity: 1 !important;
        }

        @media (max-width: 1200px) {
          .admin-appbar-buttons {
            max-width: calc(100% - 180px) !important;
          }
        }

        @media (max-width: 1024px) {
          .admin-appbar-buttons {
            gap: 6px !important;
            max-width: calc(100% - 160px) !important;
          }
          .admin-appbar-buttons button {
            padding: 10px !important;
            min-width: 40px !important;
            height: 40px !important;
          }
        }

        @media (max-width: 768px) {
          .admin-appbar-center h1 {
            font-size: 16px !important;
          }
          .admin-appbar-center > div:first-child {
            width: 44px !important;
            height: 44px !important;
          }
          .admin-appbar-buttons {
            max-width: calc(100% - 140px) !important;
          }
        }

        @media (max-width: 640px) {
          .admin-appbar-buttons button {
            padding: 8px !important;
            min-width: 36px !important;
            height: 36px !important;
          }
          .admin-appbar-buttons button svg {
            width: 16px !important;
            height: 16px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminAppBar;
